/**
 * Marquee block — horizontal logo strip (Swiper).
 * Uses data-marquee-block so it does not share SliderBlock / initSliderBlocks with assets/slider.js.
 */

const initializedMarquees = new WeakSet();
const pendingMarqueeInits = [];
const MOBILE_BREAKPOINT = 768;

class MarqueeBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    if (initializedMarquees.has(element)) return;
    initializedMarquees.add(element);

    this.visibility = this.element.dataset.sliderVisibility || 'both';
    this.checkVisibilityAndInit();

    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();
    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingMarqueeInits.push(this);
      return;
    }

    this.contentEl = this.element.querySelector('.marquee-content');
    if (!this.contentEl) return;

    let slides;

    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter((el) => !excludedTags.includes(el.tagName));

      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper');

    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }

    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    slides.forEach((slide) => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);

    const spaceBetween = parseInt(this.element.dataset.spaceBetween, 10) || 120;
    const speed = parseInt(this.element.dataset.speed, 10) || 3000;

    const marqueeOptions = {
      slidesPerView: 3,
      spaceBetween,
      centeredSlides: false,
      loop: true,
      loopedSlides: 10,
      speed,
      allowTouchMove: false,
      autoplay: {
        enabled: true,
        delay: 1,
        disableOnInteraction: false,
      },
      breakpoints: {
        [MOBILE_BREAKPOINT]: {
          slidesPerView: 'auto',
          centeredSlides: true,
        },
      },
    };

    this.swiper = new Swiper(swiperEl, marqueeOptions);

    this.element.setAttribute('data-swiper-initialized', 'true');

    this.isInitialized = true;
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach((slide) => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.element.removeAttribute('data-swiper-initialized');
    this.isInitialized = false;
  }

  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

function processPendingMarqueeInits() {
  if (!window.Swiper || pendingMarqueeInits.length === 0) return;

  while (pendingMarqueeInits.length > 0) {
    const instance = pendingMarqueeInits.shift();
    instance.init();
  }
}

function waitForSwiperMarquee() {
  if (window.Swiper) {
    processPendingMarqueeInits();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value,
      });
      processPendingMarqueeInits();
    },
  });
}

function initMarqueeBlocks() {
  document.querySelectorAll('[data-marquee-block]').forEach((el) => {
    new MarqueeBlock(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initMarqueeBlocks();
    waitForSwiperMarquee();
  });
} else {
  initMarqueeBlocks();
  waitForSwiperMarquee();
}

window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initMarqueeBlocks();
  }
});

document.addEventListener('shopify:section:load', () => {
  initMarqueeBlocks();
  processPendingMarqueeInits();
});

window.MarqueeBlock = MarqueeBlock;
window.initMarqueeBlocks = initMarqueeBlocks;
